import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Search,
  Lock,
  RotateCw,
  LockOpen,
  Loader2,
  Check,
  X,
  User,
  Download,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  cajaBancosAPI,
  CierreDiario,
  CierreDiarioDetalle,
  Factura,
  ventasAPI,
  type Sucursal,
} from "@/lib/api";
import { formatDateOnlyEsAR, toDateOnlyString } from "@/lib/utils";
import { dataRefresh } from "@/lib/dataRefresh";
import { toast } from "sonner";
import { Pagination } from "@/components/ui/pagination";
import { runWithProgressToast } from "@/components/ui/progress-toast";
import { CAJA_LISTADO_PAGE_SIZE, paginateListado } from "./cajaHistorico";
import {
  CIERRE_DATE_PRESET_DEFAULT,
  CIERRE_DATE_PRESETS,
  cierreMatchesDatePreset,
  isCierreDatePreset,
  type CierreDatePreset,
} from "./cajaTabs";
import {
  DetalleSortKey,
  cierreDolaresCardView,
  electronico,
  readCierreCardAmounts,
  detalleSinImporte,
  sortCierreDetalles,
  totalBrutoCaja,
  totalNetoCaja,
} from "./cierreDiarioCards";
import {
  detalleEsUsd,
  dolaresDesdePesos,
  tasaIngresada,
  usdDhDisplayAmount,
} from "./cierreDetalleUsd";
import { shouldAdoptParentCierreDetalle } from "./cierreDetalleSync";
import { cierreDetalleDestinatario } from "./cierreDestinatario";

/** F4: factura asociada al movimiento (API facturaId o origen factura:{pk}). */
function resolveDetalleFacturaId(mov: CierreDiarioDetalle): number | null {
  const fromApi = mov.facturaId ?? mov.factura_id;
  if (typeof fromApi === "number" && Number.isFinite(fromApi) && fromApi > 0) {
    return fromApi;
  }
  const key = String(mov.origenKey ?? mov.origen_key ?? "");
  const m = /^factura:(\d+)/.exec(key);
  if (m) return Number(m[1]);
  return null;
}

function origenKey(mov: CierreDiarioDetalle): string {
  return String(mov.origenKey ?? mov.origen_key ?? "");
}

function resolveDetalleMovimientoCajaId(mov: CierreDiarioDetalle): number | null {
  const m = /^movimiento_caja:(\d+)/.exec(origenKey(mov));
  return m ? Number(m[1]) : null;
}

function resolveDetalleFondoId(mov: CierreDiarioDetalle): number | null {
  const m = /^fondo_movimiento:(\d+)/.exec(origenKey(mov));
  return m ? Number(m[1]) : null;
}

function isMovimientoDetalleClickable(mov: CierreDiarioDetalle): boolean {
  return resolveDetalleMovimientoCajaId(mov) != null || resolveDetalleFondoId(mov) != null;
}

function resolveUsuarioNombre(
  source?: {
    usuarioNombre?: string | null;
    usuario_nombre?: string | null;
    usuarioUsername?: string | null;
    usuario_username?: string | null;
  } | null,
): string | null {
  if (!source) return null;
  const raw =
    source.usuarioNombre ||
    source.usuario_nombre ||
    source.usuarioUsername ||
    source.usuario_username ||
    "";
  const name = String(raw).trim();
  return name || null;
}

function UsuarioGeneradorBadge({ nombre }: { nombre: string | null }) {
  return (
    <Badge variant="secondary" className="rounded-full font-normal">
      <User className="h-3 w-3" aria-hidden="true" />
      {nombre || "Sin usuario"}
    </Badge>
  );
}

type DetalleSortState = {
  key: DetalleSortKey | "default";
  dir: "asc" | "desc";
};

type VerificacionEstado = "" | "ok" | "rechazado";

function resolveVerificacionEstado(detalle: CierreDiarioDetalle): VerificacionEstado {
  const raw = detalle.verificacionEstado ?? detalle.verificacion_estado;
  if (raw === "ok" || raw === "rechazado" || raw === "") return raw;
  return detalle.verificado ? "ok" : "";
}

function nextVerificacionEstado(current: VerificacionEstado): VerificacionEstado {
  if (current === "") return "ok";
  if (current === "ok") return "rechazado";
  return "";
}

interface CierreDiarioListadoProps {
  cierres: CierreDiario[];
  sucursales?: Sucursal[];
  onFinalizarCierre?: (cierreId: string) => void;
  onRefresh?: (cierre: CierreDiario) => void;
  onCierreSilentRefresh?: (cierre: CierreDiario) => void;
  isReadOnly?: boolean;
  /** Histórico ya recorta por API: no repetir hoy/semana/mes ni mostrar Todos. */
  hideDateFilter?: boolean;
  discardEditsToken?: number;
  onDirtyStateChange?: (detalleId: number, dirty: boolean) => void;
}

interface DetailAuditCellsProps {
  cierreId: number;
  detalle: CierreDiarioDetalle;
  disabled: boolean;
  discardEditsToken: number;
  onDirtyStateChange?: (detalleId: number, dirty: boolean) => void;
  onPersisted?: () => void;
}

const METODOS_VENTA = [
  ["efectivo", "Efectivo"],
  ["tarjeta_debito", "Tarjeta débito"],
  ["tarjeta_credito", "Tarjeta crédito"],
  ["transferencia", "Transferencia"],
  ["cuenta corriente", "Cuenta corriente"],
  ["dolares", "Dólares"],
] as const;

const METODOS_CAJA = METODOS_VENTA.filter(
  ([codigo]) => codigo !== "cuenta corriente" && codigo !== "dolares",
);

function canonMetodoCierre(raw: unknown): string {
  const s = String(raw || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ");
  if (s.includes("cuenta") && s.includes("corriente")) return "cuenta corriente";
  if (s.includes("dolar")) return "dolares";
  if (s.includes("debito")) return "tarjeta_debito";
  if (s.includes("credito") || s === "tarjeta") return "tarjeta_credito";
  if (s.includes("transfer")) return "transferencia";
  if (s.includes("efectivo")) return "efectivo";
  if (s.includes("cheque")) return "cheque";
  if (s.includes("saldo")) return "saldo";
  return "";
}

function etiquetaMetodoVenta(codigo: string): string {
  return METODOS_VENTA.find(([value]) => value === codigo)?.[1] ?? codigo;
}

function importePesosDelMovimiento(mov: CierreDiarioDetalle): number {
  const raw = mov.importeArs ?? mov.importe_ars ?? mov.importe;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function origenDelDetalle(mov: CierreDiarioDetalle) {
  return String(mov.origenKey || mov.origen_key || "");
}

function metodosParaDetalle(mov: CierreDiarioDetalle) {
  return origenDelDetalle(mov).startsWith("movimiento_caja:") ? METODOS_CAJA : METODOS_VENTA;
}

function textoCambioMetodo(mov: CierreDiarioDetalle, metodo: string) {
  const destino = etiquetaMetodoVenta(metodo);
  const key = origenDelDetalle(mov);
  if (key.startsWith("recibo_pago:")) {
    return `El recibo queda en ${destino} y el día abierto también. El importe no cambia.`;
  }
  if (key.startsWith("proveedor_pago:")) {
    return `El pago a proveedor queda en ${destino} y el día abierto también. El importe no cambia.`;
  }
  if (key.startsWith("movimiento_caja:")) {
    return `El movimiento de caja queda en ${destino} y el día abierto también. El importe no cambia.`;
  }
  return `No se emite otra factura. Número, CAE e importe quedan iguales. El día abierto pasa este cobro a ${destino}.`;
}

function detalleMetodoVentaEditable(
  cierre: CierreDiario,
  mov: CierreDiarioDetalle,
  isReadOnly: boolean,
): boolean {
  if (isReadOnly || cierre.cerrado) return false;
  const key = origenDelDetalle(mov);
  const tipo = String(mov.tipo || "");
  if (key.includes("sin_importe") || key.includes("vuelto")) return false;
  const venta = key.startsWith("factura_pago:") || key.startsWith("factura:");
  const recibo = key.startsWith("recibo_pago:");
  const pago = key.startsWith("proveedor_pago:");
  const caja = key.startsWith("movimiento_caja:");
  if (!venta && !recibo && !pago && !caja) return false;
  if (venta && tipo !== "factura" && tipo !== "cobro_usd") return false;
  const metodo = canonMetodoCierre(mov.metodoPago ?? mov.metodo_pago);
  return metodosParaDetalle(mov).some(([codigo]) => codigo === metodo);
}

function DetailAuditCells({
  cierreId,
  detalle,
  disabled,
  discardEditsToken,
  onDirtyStateChange,
  onPersisted,
}: DetailAuditCellsProps) {
  const detalleId = detalle.id as number;
  const [persisted, setPersisted] = useState(detalle);
  const [estado, setEstado] = useState<VerificacionEstado>(resolveVerificacionEstado(detalle));
  const [observacion, setObservacion] = useState(detalle.observacion || "");
  const [dirty, setDirty] = useState(false);
  const [checking, setChecking] = useState(false);
  const [savingObservation, setSavingObservation] = useState(false);
  const [error, setError] = useState("");
  const skipNextBlur = useRef(false);
  const persistRef = useRef(persisted);
  persistRef.current = persisted;
  const observacionRef = useRef(observacion);
  observacionRef.current = observacion;
  const saveChainRef = useRef(Promise.resolve());

  const version = persisted.updatedAt || persisted.updated_at || "";

  const patchDetalle = (payload: {
    verificacionEstado?: VerificacionEstado;
    observacion?: string;
  }) => {
    const run = async () => {
      const current = persistRef.current;
      const updatedAt = current.updatedAt || current.updated_at || "";
      const updated = await cajaBancosAPI.updateCierreDetalle(cierreId, detalleId, {
        ...payload,
        updatedAt,
      });
      persistRef.current = updated;
      setPersisted(updated);
      setObservacion(updated.observacion || "");
      setEstado(resolveVerificacionEstado(updated));
      setDirty(false);
      setError("");
      return updated;
    };
    const queued = saveChainRef.current.then(run, run);
    saveChainRef.current = queued.then(
      () => undefined,
      () => undefined,
    );
    return queued;
  };

  useEffect(() => {
    if (
      !shouldAdoptParentCierreDetalle({
        dirty,
        localUpdatedAt: persistRef.current.updatedAt || persistRef.current.updated_at,
        parentUpdatedAt: detalle.updatedAt || detalle.updated_at,
      })
    ) {
      return;
    }
    setPersisted(detalle);
    setEstado(resolveVerificacionEstado(detalle));
    setObservacion(detalle.observacion || "");
  }, [detalle, dirty]);

  useEffect(() => {
    setPersisted(detalle);
    setEstado(resolveVerificacionEstado(detalle));
    setObservacion(detalle.observacion || "");
    setDirty(false);
    setError("");
  }, [discardEditsToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onDirtyStateChange?.(detalleId, dirty);
    return () => onDirtyStateChange?.(detalleId, false);
  }, [detalleId, dirty, onDirtyStateChange]);

  const saveEstado = async (nextEstado: VerificacionEstado) => {
    const previous = estado;
    setEstado(nextEstado);
    setChecking(true);
    setError("");
    try {
      const obs = observacionRef.current.trim();
      const payload: {
        verificacionEstado: VerificacionEstado;
        observacion?: string;
      } = { verificacionEstado: nextEstado };
      if (obs !== (persistRef.current.observacion || "")) {
        payload.observacion = obs;
      }
      await patchDetalle(payload);
      onPersisted?.();
    } catch {
      setEstado(previous);
      setError("No se pudo guardar la verificación.");
      toast.error("No se pudo guardar la verificación");
    } finally {
      setChecking(false);
    }
  };

  const persistObservation = async () => {
    const normalized = observacionRef.current.trim();
    if (normalized === (persistRef.current.observacion || "")) {
      setObservacion(normalized);
      setDirty(false);
      return;
    }
    setSavingObservation(true);
    setError("");
    try {
      await patchDetalle({ observacion: normalized });
      onPersisted?.();
    } catch {
      setObservacion(persistRef.current.observacion || "");
      setDirty(false);
      setError("No se pudo guardar la observación.");
      toast.error("No se pudo guardar la observación");
    } finally {
      setSavingObservation(false);
    }
  };

  const saveObservation = async () => {
    if (skipNextBlur.current) {
      skipNextBlur.current = false;
      return;
    }
    await persistObservation();
  };

  const estadoAria =
    estado === "ok"
      ? `Aprobado — clic para rechazar detalle ${detalle.referencia || detalleId}`
      : estado === "rechazado"
        ? `Rechazado — clic para limpiar verificación del detalle ${detalle.referencia || detalleId}`
        : `Sin verificar — clic para aprobar detalle ${detalle.referencia || detalleId}`;

  return (
    <>
      <td className="py-2 px-3 text-xs text-center">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={`h-7 w-7 shrink-0 ${
            estado === "ok"
              ? "border-green-600 text-green-600 hover:bg-green-50 dark:hover:bg-green-950"
              : estado === "rechazado"
                ? "border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                : "text-muted-foreground"
          } ${checking ? "opacity-50" : ""}`}
          disabled={disabled || checking || savingObservation || !version}
          aria-label={estadoAria}
          aria-busy={checking}
          title={
            estado === "ok"
              ? "Aprobado (clic → rechazado)"
              : estado === "rechazado"
                ? "Rechazado (clic → pendiente)"
                : "Pendiente (clic → aprobado)"
          }
          onMouseDown={() => {
            skipNextBlur.current = true;
          }}
          onClick={() => void saveEstado(nextVerificacionEstado(estado))}
        >
          {estado === "ok" ? (
            <Check className="h-4 w-4" aria-hidden="true" />
          ) : estado === "rechazado" ? (
            <X className="h-4 w-4" aria-hidden="true" />
          ) : (
            <span className="h-3.5 w-3.5 rounded-sm border border-muted-foreground/50" aria-hidden="true" />
          )}
        </Button>
      </td>
      <td className="py-2 px-3 text-xs min-w-[180px]">
        <div className="relative">
          <Input
            value={observacion}
            maxLength={50}
            placeholder="Observaciones"
            disabled={disabled || checking || !version}
            aria-label={`Observaciones del detalle ${detalle.referencia || detalleId}`}
            aria-invalid={Boolean(error)}
            className="h-8 pr-7 text-xs"
            onChange={(event) => {
              setObservacion(event.target.value);
              setDirty(event.target.value !== (persisted.observacion || ""));
              setError("");
            }}
            onBlur={() => void saveObservation()}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                skipNextBlur.current = true;
                const input = event.currentTarget;
                void persistObservation().then(() => input.blur());
              } else if (event.key === "Tab") {
                skipNextBlur.current = true;
                void persistObservation();
              } else if (event.key === "Escape") {
                event.preventDefault();
                skipNextBlur.current = true;
                setObservacion(persisted.observacion || "");
                setDirty(false);
                setError("");
                event.currentTarget.blur();
              }
            }}
          />
          {savingObservation && (
            <Loader2 className="absolute right-2 top-2 h-4 w-4 animate-spin" aria-hidden="true" />
          )}
        </div>
        {error && <p className="mt-1 text-[11px] text-destructive" role="alert">{error}</p>}
      </td>
    </>
  );
}

export function CierreDiarioListado({
  cierres,
  sucursales = [],
  onFinalizarCierre,
  onRefresh,
  onCierreSilentRefresh,
  isReadOnly = false,
  hideDateFilter = false,
  discardEditsToken = 0,
  onDirtyStateChange,
}: CierreDiarioListadoProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [guardandoMetodo, setGuardandoMetodo] = useState(false);
  const [metodoConfirm, setMetodoConfirm] = useState<{
    cierre: CierreDiario;
    mov: CierreDiarioDetalle;
    metodo: string;
  } | null>(null);
  const [metodoUsd, setMetodoUsd] = useState<{
    cierre: CierreDiario;
    mov: CierreDiarioDetalle;
    cotizacion: string;
    importeArs: number;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState<CierreDatePreset>(CIERRE_DATE_PRESET_DEFAULT);
  const [sucursalFilter, setSucursalFilter] = useState("todas");
  const [page, setPage] = useState(1);
  // F8/Req12: sort UI del detalle — independiente por cierre (no global).
  const [detalleSortByCierre, setDetalleSortByCierre] = useState<
    Record<number, DetalleSortState>
  >({});
  // F4: modal detalle factura (mismo patrón «Ver» de ventas). F3: tag usuario.
  const [showFacturaDialog, setShowFacturaDialog] = useState(false);
  const [viewingFactura, setViewingFactura] = useState<Factura | null>(null);
  const [viewingDetalle, setViewingDetalle] = useState<CierreDiarioDetalle | null>(null);
  const [showMovimientoDialog, setShowMovimientoDialog] = useState(false);
  const [loadingFacturaId, setLoadingFacturaId] = useState<number | null>(null);
  const [exportingId, setExportingId] = useState<number | null>(null);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, dateFilter, sucursalFilter, hideDateFilter]);

  const closeFacturaDialog = () => {
    setShowFacturaDialog(false);
    setViewingFactura(null);
    setViewingDetalle(null);
  };

  const closeMovimientoDialog = () => {
    setShowMovimientoDialog(false);
    setViewingDetalle(null);
  };

  const openConceptoDetalle = async (mov: CierreDiarioDetalle) => {
    const facturaId = resolveDetalleFacturaId(mov);
    if (facturaId) {
      setLoadingFacturaId(facturaId);
      setViewingDetalle(mov);
      try {
        const factura = await ventasAPI.getFactura(facturaId);
        setViewingFactura(factura);
        setShowFacturaDialog(true);
      } catch {
        setViewingDetalle(null);
        toast.error("No se pudo cargar el detalle de la factura.");
      } finally {
        setLoadingFacturaId(null);
      }
      return;
    }
    if (isMovimientoDetalleClickable(mov)) {
      setViewingDetalle(mov);
      setShowMovimientoDialog(true);
      return;
    }
    toast.message("Este movimiento no tiene factura asociada.");
  };

  const exportCierre = async (cierre: CierreDiario, kind: "xlsx" | "pdf") => {
    const id = cierre.id || 0;
    if (!id) return;
    if (exportingId != null) {
      toast.message("Ya hay una exportación en curso", { position: "bottom-right" });
      return;
    }
    setExportingId(id);
    try {
      await runWithProgressToast({
        id: "cierre-export-progress",
        title: kind === "xlsx" ? "Generando Excel…" : "Generando PDF…",
        stages: [
          {
            label: kind === "xlsx" ? "Armando planilla…" : "Armando documento…",
            targetPct: 90,
          },
        ],
        job: async () => {
          const full = await cajaBancosAPI.getCierre(id);
          const mod = await import("./cierreDiarioExport");
          return mod.downloadCierreExport(full, kind);
        },
      });
      toast.success(kind === "xlsx" ? "Excel generado" : "PDF generado", {
        position: "bottom-right",
      });
    } catch {
      toast.error("No se pudo exportar este cierre.");
    } finally {
      setExportingId(null);
    }
  };

  const getDetalleSort = (cierreId: number): DetalleSortState =>
    detalleSortByCierre[cierreId] ?? { key: "default", dir: "asc" };

  const cycleDetalleSort = (cierreId: number, key: DetalleSortKey) => {
    setDetalleSortByCierre((prev) => {
      const cur = prev[cierreId] ?? { key: "default" as const, dir: "asc" as const };
      if (cur.key !== key) return { ...prev, [cierreId]: { key, dir: "asc" } };
      if (cur.dir === "asc") return { ...prev, [cierreId]: { key, dir: "desc" } };
      return { ...prev, [cierreId]: { key: "default", dir: "asc" } };
    });
  };

  const resetDetalleSort = (cierreId: number) => {
    setDetalleSortByCierre((prev) => ({
      ...prev,
      [cierreId]: { key: "default", dir: "asc" },
    }));
  };

  const sortHeaderLabel = (
    cierreId: number,
    label: string,
    key: DetalleSortKey,
  ) => {
    const { key: activeKey, dir } = getDetalleSort(cierreId);
    if (activeKey !== key) return label;
    return `${label} ${dir === "asc" ? "↑" : "↓"}`;
  };

  const formatDate = (dateString: string) => formatDateOnlyEsAR(dateString);

  const formatDateTime = (dateString: string) => {
    // Timestamps con hora: OK parsear; DateField solo-día → formatDateOnlyEsAR.
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(dateString || "").trim())) {
      return formatDateOnlyEsAR(dateString);
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return formatDateOnlyEsAR(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const toLocalYmd = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatCurrency = (amount: number | undefined | null) => {
    const safeAmount = Number(amount) || 0;
    return `$${safeAmount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatUsd = (amount: number | undefined | null) => {
    const safeAmount = Number(amount) || 0;
    return `USD ${safeAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const aplicarMetodoVenta = async (opts: {
    cierre: CierreDiario;
    mov: CierreDiarioDetalle;
    metodo: string;
    montoUsd?: string;
    cotizacion?: string;
  }) => {
    if (opts.cierre.id == null) return;
    const origen = String(opts.mov.origenKey || opts.mov.origen_key || "");
    setGuardandoMetodo(true);
    try {
      await cajaBancosAPI.cambiarMetodoVenta(opts.cierre.id, {
        origenKey: origen,
        metodoPago: opts.metodo,
        ...(opts.montoUsd != null ? { montoUsd: opts.montoUsd } : {}),
        ...(opts.cotizacion != null ? { cotizacion: opts.cotizacion } : {}),
      });
      toast.success("Método actualizado en el cierre abierto.");
      if (origen.startsWith("recibo_pago:")) dataRefresh.emit("recibos");
      onCierreSilentRefresh?.(opts.cierre);
      setMetodoConfirm(null);
      setMetodoUsd(null);
    } catch (error: { message?: string; error?: string }) {
      toast.error(error?.message || error?.error || "No se pudo cambiar el método.");
    } finally {
      setGuardandoMetodo(false);
    }
  };

  const toggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const sucursalIdOf = (cierre: CierreDiario): number | null => {
    const raw = cierre.sucursalId ?? cierre.sucursal_id ?? cierre.sucursal;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const sucursalMeta = (cierre: CierreDiario) => {
    const sid = sucursalIdOf(cierre);
    const fromList = sucursales.find((s) => Number(s.id) === sid);
    const nombre =
      cierre.sucursalNombre ||
      cierre.sucursal_nombre ||
      fromList?.nombre ||
      "";
    const esCc = Boolean(
      cierre.esCasaCentral ??
        cierre.es_casa_central ??
        fromList?.esCasaCentral ??
        fromList?.es_casa_central,
    );
    return { sid, nombre, esCc };
  };

  // Filter by search query
  const filteredBySearch = searchQuery.trim()
    ? cierres.filter(cierre => {
      const searchLower = searchQuery.toLowerCase();
      const meta = sucursalMeta(cierre);
      return (
        (cierre.id?.toString() || "").includes(searchLower) ||
        formatDate(cierre.fecha).includes(searchLower) ||
        (meta.nombre || "").toLowerCase().includes(searchLower) ||
        (meta.esCc && "casa central".includes(searchLower)) ||
        (cierre.detalles || []).some((m: CierreDiarioDetalle) =>
          (m.descripcion || "").toLowerCase().includes(searchLower) ||
          (m.referencia || "").toLowerCase().includes(searchLower)
        )
      );
    })
    : cierres;

  // Filter by date (YMD calendar, no UTC shift on DateField). Sin «Todos»: default hoy.
  const todayYmd = toLocalYmd(new Date());
  const filteredByDate = hideDateFilter
    ? filteredBySearch
    : filteredBySearch.filter((cierre) => {
      const cierreYmd = toDateOnlyString(cierre.fecha) || "";
      return cierreMatchesDatePreset(cierreYmd, dateFilter, todayYmd);
    });

  const filteredCierres = sucursalFilter === "todas"
    ? filteredByDate
    : filteredByDate.filter((cierre) => String(sucursalIdOf(cierre)) === sucursalFilter);

  const paged = paginateListado(filteredCierres, page, CAJA_LISTADO_PAGE_SIZE);

  if (cierres.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No hay cierres diarios registrados
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Buscar por ID, fecha, sucursal o concepto..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        {!hideDateFilter && (
        <Select
          value={dateFilter}
          onValueChange={(value) => {
            if (isCierreDatePreset(value)) setDateFilter(value);
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Filtrar por fecha" />
          </SelectTrigger>
          <SelectContent>
            {CIERRE_DATE_PRESETS.map((preset) => (
              <SelectItem key={preset.value} value={preset.value}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        )}
        <Select value={sucursalFilter} onValueChange={setSucursalFilter}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Filtrar por sucursal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las sucursales</SelectItem>
            {sucursales.filter((s) => s.id).map((s) => (
              <SelectItem key={s.id} value={String(s.id)}>
                {s.nombre}
                {(s.esCasaCentral || s.es_casa_central) ? " (Casa central)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="w-8"></th>
              <th className="text-left py-3 px-4 font-medium text-sm">ID</th>
              <th className="text-left py-3 px-4 font-medium text-sm">Fecha</th>
              <th className="text-left py-3 px-4 font-medium text-sm">Sucursal</th>
              <th className="text-right py-3 px-4 font-medium text-sm">Total Ingresos</th>
              <th className="text-right py-3 px-4 font-medium text-sm">Total Gastos</th>
              <th className="text-right py-3 px-4 font-medium text-sm">Total Neto</th>
              <th className="text-right py-3 px-4 font-medium text-sm">Total Bruto</th>
              <th className="text-left py-3 px-4 font-medium text-sm">Estado</th>
              <th className="w-12 py-3 px-2 font-medium text-sm">
                <span className="sr-only">Exportar</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paged.items.map((cierre) => {
              const id = cierre.id || 0;
              const listCards = readCierreCardAmounts(cierre as unknown as Record<string, unknown>);
              const listNeto = totalNetoCaja(listCards);
              const listBruto = totalBrutoCaja(listCards);
              const detalleSort = getDetalleSort(id);
              return (
                <React.Fragment key={id}>
                  <tr className={`border-b border-border hover:bg-muted/50 ${cierre.cerrado ? 'bg-muted/20' : ''}`}>
                    <td className="py-3 px-2">
                      <button
                        onClick={() => toggleRow(id)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        {expandedRows.has(id) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-sm font-mono">{id}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className="inline-flex items-center gap-2 flex-wrap">
                        {formatDate(cierre.fecha)}
                        {sucursalMeta(cierre).esCc ? (
                          <Badge variant="secondary" className="text-[10px]">
                            Casa central
                          </Badge>
                        ) : null}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">{sucursalMeta(cierre).nombre || "—"}</td>
                    <td className="py-3 px-4 text-sm text-right text-green-600 dark:text-green-400">
                      {formatCurrency(cierre.totalIngresos)}
                    </td>
                    <td className="py-3 px-4 text-sm text-right text-red-600 dark:text-red-400">
                      {formatCurrency(cierre.totalGastos)}
                    </td>
                    <td className="py-3 px-4 text-sm text-right font-semibold">
                      <span className={
                        listNeto > 0
                          ? 'text-green-600 dark:text-green-400'
                          : listNeto < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-yellow-600 dark:text-yellow-400'
                      }>
                        {formatCurrency(listNeto)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-right font-semibold">
                      <span className={
                        listBruto > 0
                          ? 'text-green-600 dark:text-green-400'
                          : listBruto < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-yellow-600 dark:text-yellow-400'
                      }>
                        {formatCurrency(listBruto)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {cierre.cerrado ? (
                        <span className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 w-fit">
                          <Lock className="h-3 w-3" />
                          Cerrado
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          Abierto
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            title="Exportar este cierre"
                            aria-label="Exportar este cierre"
                            disabled={exportingId != null}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {exportingId === id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem
                            onSelect={() => {
                              void exportCierre(cierre, "xlsx");
                            }}
                          >
                            Excel (XLSX)
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onSelect={() => {
                              void exportCierre(cierre, "pdf");
                            }}
                          >
                            PDF
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                  {expandedRows.has(id) && (
                    <tr>
                      <td colSpan={10} className="bg-muted/30 p-4 w-full">
                        <div className="w-full min-w-0 space-y-4">
                          {/* Summary Section — F8 totals + F3b: 1 electrónico, labels sin (neto)/(bruto) */}
                          {(() => {
                            const cards = listCards;
                            const elec = electronico(cards);
                            const netoCaja = listNeto;
                            const brutoCaja = listBruto;
                            const usdCard = cierreDolaresCardView(cards);
                            // F2 #1: flex-wrap + min basis so ≥8-digit amounts don't collide inside the card row
                            return (
                          <div className="w-full flex flex-wrap items-start gap-x-4 gap-y-3 p-4 bg-background rounded-lg border border-border">
                            <div className="min-w-[11rem] flex-[1_1_11rem] max-w-full">
                              <p className="text-xs text-muted-foreground leading-snug">Saldo Inicial</p>
                              <p className="text-[clamp(0.95rem,2.2vw,1.125rem)] font-semibold tabular-nums break-words text-gray-600 dark:text-gray-400">
                                {formatCurrency(cards.saldoInicial)}
                              </p>
                            </div>
                            <div className="min-w-[11rem] flex-[1_1_11rem] max-w-full">
                              <p className="text-xs text-muted-foreground leading-snug">Efectivo</p>
                              <p className="text-[clamp(0.95rem,2.2vw,1.125rem)] font-semibold tabular-nums break-words text-blue-600 dark:text-blue-400">
                                {formatCurrency(cards.efectivoIngresos - cards.efectivoGastos)}
                              </p>
                            </div>
                            <div className="min-w-[11rem] flex-[1_1_11rem] max-w-full">
                              <p className="text-xs text-muted-foreground leading-snug">Cheques</p>
                              <p className="text-[clamp(0.95rem,2.2vw,1.125rem)] font-semibold tabular-nums break-words text-purple-600 dark:text-purple-400">
                                {formatCurrency(cards.chequesIngresos - cards.chequesGastos)}
                              </p>
                            </div>
                            <div className="min-w-[11rem] flex-[1_1_11rem] max-w-full">
                              <p className="text-xs text-muted-foreground leading-snug">Electrónico</p>
                              <p className="text-[clamp(0.95rem,2.2vw,1.125rem)] font-semibold tabular-nums break-words text-cyan-600 dark:text-cyan-400">
                                {formatCurrency(elec)}
                              </p>
                            </div>
                            <div className="min-w-[11rem] flex-[1_1_11rem] max-w-full">
                              <p className="text-xs text-muted-foreground leading-snug">Cta Cte</p>
                              <p className="text-[clamp(0.95rem,2.2vw,1.125rem)] font-semibold tabular-nums break-words text-amber-600 dark:text-amber-400">
                                {formatCurrency(cards.totalFacturas)}
                              </p>
                            </div>
                            <div className="min-w-[11rem] flex-[1_1_11rem] max-w-full">
                              <p className="text-xs text-muted-foreground leading-snug">Dólares</p>
                              <p className="text-[clamp(0.95rem,2.2vw,1.125rem)] font-semibold tabular-nums break-words text-emerald-700 dark:text-emerald-400">
                                {formatUsd(usdCard.amount)}
                              </p>
                              {usdCard.caption ? (
                                <p className="text-[10px] text-muted-foreground tabular-nums mt-0.5">
                                  {usdCard.caption}
                                </p>
                              ) : null}
                            </div>
                            <div className="min-w-[11rem] flex-[1_1_11rem] max-w-full">
                              <p className="text-xs text-muted-foreground leading-snug">Total Neto</p>
                              <p className={`text-[clamp(0.95rem,2.2vw,1.125rem)] font-bold tabular-nums break-words ${netoCaja >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(netoCaja)}
                              </p>
                            </div>
                            <div className="min-w-[11rem] flex-[1_1_11rem] max-w-full">
                              <p className="text-xs text-muted-foreground leading-snug">Total Bruto</p>
                              <p className={`text-[clamp(0.95rem,2.2vw,1.125rem)] font-bold tabular-nums break-words ${brutoCaja >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(brutoCaja)}
                              </p>
                            </div>
                          </div>
                            );
                          })()}

                          {/* Finalization Info */}
                          {cierre.cerrado && cierre.fechaCierre && (
                            <div className="p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg">
                              <div className="flex items-center gap-2 mb-2">
                                <Lock className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                                <span className="font-semibold text-sm">Cierre Finalizado</span>
                              </div>
                              <div className="text-xs text-muted-foreground space-y-1">
                                <p>Fecha de cierre: {formatDateTime(cierre.fechaCierre)}</p>
                                <p className="italic">Este cierre no puede ser modificado.</p>
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          {!cierre.cerrado && (
                            <div className="flex justify-end gap-2">
                              {onRefresh && (
                                <Button
                                  onClick={() => onRefresh(cierre)}
                                  variant="outline"
                                  size="sm"
                                  title="Actualizar Datos"
                                  className="h-8 w-8 p-0 flex items-center justify-center group"
                                >
                                  <RotateCw className="h-4 w-4 transition-transform duration-500 group-hover:rotate-180" />
                                </Button>
                              )}
                              {onFinalizarCierre && !isReadOnly && (
                                <Button
                                  onClick={() => onFinalizarCierre(id.toString())}
                                  variant="default"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:w-24 transition-all duration-300 bg-gradient-to-br from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white flex items-center justify-start group overflow-hidden"
                                  title="Finalizar Cierre Diario"
                                >
                                  <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center">
                                    <div className="relative h-4 w-4">
                                      <LockOpen className="h-4 w-4 absolute inset-0 transition-opacity duration-200 group-hover:opacity-0" />
                                      <Lock className="h-4 w-4 absolute inset-0 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
                                    </div>
                                  </div>
                                  <span className="max-w-0 opacity-0 group-hover:max-w-[60px] group-hover:opacity-100 transition-all duration-300 overflow-hidden whitespace-nowrap text-xs font-medium group-hover:ml-1">
                                    Cerrar
                                  </span>
                                </Button>
                              )}
                            </div>
                          )}

                          <div className="flex items-center justify-between gap-2">
                            <h4 className="font-semibold text-sm">Detalle de Operaciones:</h4>
                            {detalleSort.key !== "default" && (
                              <button
                                type="button"
                                className="text-[11px] text-muted-foreground underline"
                                onClick={() => resetDetalleSort(id)}
                              >
                                Orden por defecto
                              </button>
                            )}
                          </div>
                          <div className="w-full border border-border rounded-lg overflow-hidden">
                            <table className="w-full">
                              <thead className="bg-muted">
                                <tr>
                                  <th className="text-left py-2 px-3 text-xs font-medium">
                                    <button type="button" className="hover:underline" onClick={() => cycleDetalleSort(id, "descripcion")}>
                                      {sortHeaderLabel(id, "Concepto", "descripcion")}
                                    </button>
                                  </th>
                                  <th className="text-left py-2 px-3 text-xs font-medium">
                                    <button type="button" className="hover:underline" onClick={() => cycleDetalleSort(id, "referencia")}>
                                      {sortHeaderLabel(id, "Referencia", "referencia")}
                                    </button>
                                  </th>
                                  <th className="text-left py-2 px-3 text-xs font-medium">
                                    <button type="button" className="hover:underline" onClick={() => cycleDetalleSort(id, "tipo")}>
                                      {sortHeaderLabel(id, "Tipo", "tipo")}
                                    </button>
                                  </th>
                                  <th className="text-left py-2 px-3 text-xs font-medium">
                                    <button type="button" className="hover:underline" onClick={() => cycleDetalleSort(id, "metodo")}>
                                      {sortHeaderLabel(id, "Método Pago", "metodo")}
                                    </button>
                                  </th>
                                  <th className="text-right py-2 px-3 text-xs font-medium">
                                    <button type="button" className="hover:underline" onClick={() => cycleDetalleSort(id, "debe")}>
                                      {sortHeaderLabel(id, "Debe (ARS)", "debe")}
                                    </button>
                                  </th>
                                  <th className="text-right py-2 px-3 text-xs font-medium">D/H (USD)</th>
                                  <th className="text-right py-2 px-3 text-xs font-medium">
                                    <button type="button" className="hover:underline" onClick={() => cycleDetalleSort(id, "haber")}>
                                      {sortHeaderLabel(id, "Haber (ARS)", "haber")}
                                    </button>
                                  </th>
                                  <th className="text-center py-2 px-3 text-xs font-medium">Verif.</th>
                                  <th className="text-left py-2 px-3 text-xs font-medium">Observación</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortCierreDetalles(
                                  cierre.detalles || [],
                                  detalleSort.key,
                                  detalleSort.dir,
                                ).map((mov) => {
                                  const esIngreso = mov.esIngreso ?? mov.es_ingreso ?? false;
                                  const sinImporte = detalleSinImporte(mov);
                                  const metodo = mov.metodoPago ?? mov.metodo_pago;
                                  const facturaId = resolveDetalleFacturaId(mov);
                                  const movimientoClickable = isMovimientoDetalleClickable(mov);
                                  const conceptoClickable = facturaId != null || movimientoClickable;
                                  const conceptoLoading =
                                    facturaId != null && loadingFacturaId === facturaId;
                                  const esUsd = detalleEsUsd(mov);
                                  const usdDh = usdDhDisplayAmount(mov);
                                  return (
                                  <tr
                                    key={mov.id ?? mov.origenKey ?? mov.origen_key}
                                    className="border-t border-border hover:bg-muted/20"
                                  >
                                    <td className="py-2 px-3 text-xs">
                                      {conceptoClickable ? (
                                        <button
                                          type="button"
                                          onClick={() => openConceptoDetalle(mov)}
                                          disabled={conceptoLoading}
                                          className="text-left text-primary underline-offset-2 hover:underline disabled:opacity-60"
                                          title={facturaId != null ? "Ver detalle de factura" : "Ver detalle del movimiento"}
                                        >
                                          {conceptoLoading ? (
                                            <span className="inline-flex items-center gap-1">
                                              <Loader2 className="h-3 w-3 animate-spin" />
                                              {mov.descripcion}
                                            </span>
                                          ) : (
                                            mov.descripcion
                                          )}
                                        </button>
                                      ) : (
                                        mov.descripcion
                                      )}
                                    </td>
                                    <td className="py-2 px-3 text-xs">{mov.referencia || '-'}</td>
                                    <td className="py-2 px-3 text-xs">
                                      {sinImporte && !esIngreso ? (
                                        <span className="text-muted-foreground">-</span>
                                      ) : (
                                      <span className={`px-2 py-1 rounded-full text-xs ${esIngreso
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                        }`}>
                                        {esIngreso ? 'Ingreso' : 'Gasto'}
                                      </span>
                                      )}
                                    </td>
                                    <td className="py-2 px-3 text-xs capitalize">
                                      {detalleMetodoVentaEditable(cierre, mov, isReadOnly) ? (
                                        <Select
                                          value={canonMetodoCierre(metodo)}
                                          disabled={guardandoMetodo}
                                          onValueChange={(value) => {
                                            if (value === canonMetodoCierre(metodo)) return;
                                            if (value === "dolares") {
                                              setMetodoUsd({
                                                cierre,
                                                mov,
                                                cotizacion: "",
                                                importeArs: importePesosDelMovimiento(mov),
                                              });
                                              return;
                                            }
                                            setMetodoConfirm({ cierre, mov, metodo: value });
                                          }}
                                        >
                                          <SelectTrigger className="h-7 text-xs capitalize">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {metodosParaDetalle(mov).map(([codigo, label]) => (
                                              <SelectItem key={codigo} value={codigo}>
                                                {label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        metodo ? String(metodo).replace('_', ' ') : '-'
                                      )}
                                    </td>

                                    <td className="py-2 px-3 text-xs text-right font-semibold">
                                      {!sinImporte && esIngreso && !esUsd ? (
                                        <span className="text-green-600 dark:text-green-400">
                                          {formatCurrency(mov.importeArs ?? mov.importe_ars ?? mov.importe)}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    <td className="py-2 px-3 text-xs text-right font-semibold">
                                      {!sinImporte && usdDh > 0 ? (
                                        <span className={
                                          esIngreso
                                            ? "text-emerald-700 dark:text-emerald-400"
                                            : "text-red-600 dark:text-red-400"
                                        }>
                                          {formatUsd(usdDh)}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    <td className="py-2 px-3 text-xs text-right font-semibold">
                                      {!sinImporte && !esIngreso && !esUsd ? (
                                        <span className="text-red-600 dark:text-red-400">
                                          {formatCurrency(mov.importe)}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    {mov.id && (
                                      <DetailAuditCells
                                        cierreId={id}
                                        detalle={mov}
                                        disabled={cierre.cerrado || isReadOnly}
                                        discardEditsToken={discardEditsToken}
                                        onDirtyStateChange={onDirtyStateChange}
                                        onPersisted={() => onCierreSilentRefresh?.(cierre)}
                                      />
                                    )}
                                  </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredCierres.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          {searchQuery.trim() ? "No se encontraron resultados" : "No hay datos para mostrar"}
        </div>
      )}

      {filteredCierres.length > CAJA_LISTADO_PAGE_SIZE && (
        <Pagination
          currentPage={paged.page}
          totalCount={paged.total}
          pageSize={CAJA_LISTADO_PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      {/* F4: detalle factura (mismo layout que «Ver» en listado de ventas). F3: tag usuario. */}
      <Dialog
        open={showFacturaDialog}
        onOpenChange={(open) => {
          if (open) setShowFacturaDialog(true);
          else closeFacturaDialog();
        }}
      >
        <DialogContent className="w-[calc(100%-2rem)] sm:max-w-[96vw] max-h-[90vh] min-w-0 overflow-x-hidden overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap pr-8">
              <span>Detalle - Factura {viewingFactura?.numero}</span>
              <UsuarioGeneradorBadge
                nombre={
                  resolveUsuarioNombre(viewingFactura) ??
                  resolveUsuarioNombre(viewingDetalle)
                }
              />
            </DialogTitle>
          </DialogHeader>

          {viewingFactura && (
            <div className="space-y-4 min-w-0">
              <div className="min-w-0">
                <h4 className="font-semibold text-sm mb-2">Productos</h4>
                {/* Scroll vertical ~7 filas. Overflow-x en la grilla (no table-fixed) si no entra; el diálogo no scrollea en horizontal. */}
                <div className="border border-input rounded-lg overflow-x-auto overflow-y-auto max-h-[calc(2.5rem+7*2.25rem)] min-w-0">
                  <table className="w-full min-w-[640px]">
                    <thead className="bg-muted sticky top-0 z-10">
                      <tr>
                        <th className="text-left py-2 px-3 text-sm font-medium min-w-[16rem]">Producto</th>
                        <th className="text-center py-2 px-2 text-sm font-medium whitespace-nowrap">Cantidad</th>
                        <th className="text-right py-2 px-2 text-sm font-medium whitespace-nowrap">Precio Unit.</th>
                        <th className="text-center py-2 px-2 text-sm font-medium">Desc.</th>
                        <th className="text-center py-2 px-2 text-sm font-medium">Desc.</th>
                        <th className="text-right py-2 px-2 text-sm font-medium whitespace-nowrap">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewingFactura.items || []).map((item: any) => {
                        const nombre =
                          item.nombre_producto ||
                          item.nombreProducto ||
                          item.producto_nombre ||
                          item.producto?.nombre ||
                          item.nombre ||
                          "Producto";
                        const cantidad = item.cantidad || 0;
                        const precio = parseFloat(
                          String(item.precio_unitario ?? item.precioUnitario ?? 0),
                        );
                        const desc1 = parseFloat(String(item.descuento1 || 0));
                        const desc2 = parseFloat(String(item.descuento2 || 0));
                        const subtotalBase = precio * cantidad;
                        const descAmount1 = desc1 > 0 ? (subtotalBase * desc1) / 100 : 0;
                        const afterDesc1 = subtotalBase - descAmount1;
                        const descAmount2 = desc2 > 0 ? (afterDesc1 * desc2) / 100 : 0;
                        const subtotalFinal = afterDesc1 - descAmount2;
                        return (
                          <tr
                            key={item.id ?? `${nombre}-${cantidad}-${precio}`}
                            className="border-t border-input"
                          >
                            <td className="py-2 px-3 text-sm align-top whitespace-nowrap">
                              <span className="block max-w-[22rem] truncate" title={nombre}>{nombre}</span>
                            </td>
                            <td className="py-2 px-2 text-sm text-center whitespace-nowrap">{cantidad}</td>
                            <td className="py-2 px-2 text-sm text-right whitespace-nowrap">
                              ${Number(precio).toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                            </td>
                            <td className="py-2 px-2 text-sm text-center whitespace-nowrap">
                              {desc1 > 0
                                ? `${desc1}% $${descAmount1.toLocaleString("es-AR", { minimumFractionDigits: 2 })}`
                                : "-"}
                            </td>
                            <td className="py-2 px-2 text-sm text-center whitespace-nowrap">
                              {desc2 > 0
                                ? `${desc2}% $${descAmount2.toLocaleString("es-AR", { minimumFractionDigits: 2 })}`
                                : "-"}
                            </td>
                            <td className="py-2 px-2 text-sm text-right whitespace-nowrap">
                              ${subtotalFinal.toLocaleString("es-AR", { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-muted border-t-2 border-input">
                      <tr>
                        <td colSpan={5} className="py-2 px-4 text-sm font-semibold text-right">
                          Total:
                        </td>
                        <td className="py-2 px-4 text-sm text-right font-bold">
                          $
                          {Number(viewingFactura.total).toLocaleString("es-AR", {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={closeFacturaDialog}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* F3: detalle movimiento de caja / fondo (sin ítems de factura) */}
      <Dialog
        open={showMovimientoDialog}
        onOpenChange={(open) => {
          if (open) setShowMovimientoDialog(true);
          else closeMovimientoDialog();
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap pr-8">
              <span>
                Detalle - {viewingDetalle?.referencia || "Movimiento"}
              </span>
              <UsuarioGeneradorBadge nombre={resolveUsuarioNombre(viewingDetalle)} />
            </DialogTitle>
          </DialogHeader>
          {viewingDetalle && (
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Concepto</p>
                <p>{viewingDetalle.descripcion || "-"}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Tipo</p>
                  <p className="capitalize">{viewingDetalle.tipo || "-"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Método</p>
                  <p className="capitalize">
                    {String(viewingDetalle.metodoPago ?? viewingDetalle.metodo_pago ?? "-").replace("_", " ")}
                  </p>
                </div>
              </div>
              {(() => {
                const dest = cierreDetalleDestinatario(viewingDetalle);
                if (!dest.show) return null;
                return (
                  <div>
                    <p className="text-xs text-muted-foreground">Destinatario</p>
                    <p>{dest.nombre}</p>
                  </div>
                );
              })()}
              <div>
                <p className="text-xs text-muted-foreground">Importe</p>
                <p className="font-semibold tabular-nums">
                  {formatCurrency(viewingDetalle.importeArs ?? viewingDetalle.importe_ars ?? viewingDetalle.importe)}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={closeMovimientoDialog}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={metodoConfirm != null}
        onOpenChange={(open) => {
          if (!open && !guardandoMetodo) setMetodoConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cambiar método de pago</AlertDialogTitle>
            <AlertDialogDescription>
              {metodoConfirm
                ? textoCambioMetodo(metodoConfirm.mov, metodoConfirm.metodo)
                : "El día abierto pasa este cobro al método nuevo."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={guardandoMetodo}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={guardandoMetodo || metodoConfirm == null}
              onClick={(event) => {
                event.preventDefault();
                if (!metodoConfirm) return;
                void aplicarMetodoVenta({
                  cierre: metodoConfirm.cierre,
                  mov: metodoConfirm.mov,
                  metodo: metodoConfirm.metodo,
                });
              }}
            >
              {guardandoMetodo ? "Guardando…" : "Guardar método"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={metodoUsd != null}
        onOpenChange={(open) => {
          if (!open && !guardandoMetodo) setMetodoUsd(null);
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Pasar el cobro a dólares</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            No se emite otra factura. Con el tipo de cambio, el importe en pesos pasa a la columna D/H (USD).
          </p>
          {(() => {
            const tasa = tasaIngresada(metodoUsd?.cotizacion ?? "");
            const pesos = metodoUsd?.importeArs ?? 0;
            const usd = tasa != null ? dolaresDesdePesos(pesos, tasa) : 0;
            return (
              <div className="grid gap-3">
                <div className="grid gap-1.5">
                  <Label htmlFor="cierre-tipo-cambio">Tipo de cambio</Label>
                  <Input
                    id="cierre-tipo-cambio"
                    autoFocus
                    inputMode="decimal"
                    placeholder="Ej. 1450"
                    value={metodoUsd?.cotizacion ?? ""}
                    onChange={(e) =>
                      setMetodoUsd((prev) => (prev ? { ...prev, cotizacion: e.target.value } : prev))
                    }
                  />
                </div>
                <div className="grid gap-1 text-sm">
                  <span className="text-muted-foreground">Importe en pesos</span>
                  <span className="font-medium tabular-nums">{formatCurrency(pesos)}</span>
                </div>
                <div className="grid gap-1 text-sm">
                  <span className="text-muted-foreground">D/H (USD)</span>
                  <span className="font-medium tabular-nums">
                    {tasa == null ? "—" : usd > 0 ? formatUsd(usd) : "El tipo de cambio deja el importe en 0"}
                  </span>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setMetodoUsd(null)} disabled={guardandoMetodo}>
                    Cancelar
                  </Button>
                  <Button
                    disabled={guardandoMetodo || tasa == null || usd <= 0}
                    onClick={() => {
                      if (!metodoUsd || tasa == null || usd <= 0) return;
                      void aplicarMetodoVenta({
                        cierre: metodoUsd.cierre,
                        mov: metodoUsd.mov,
                        metodo: "dolares",
                        montoUsd: String(usd),
                        cotizacion: String(tasa),
                      });
                    }}
                  >
                    {guardandoMetodo ? "Guardando…" : "Pasar a D/H (USD)"}
                  </Button>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}